/**
 * Iter 245 — Catalogue produits (max 30).
 *
 * Table de 30 produits : photo, SKU, nom, prix achat/vente, fournisseur par défaut, stock total + par société.
 * CRUD modal.
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Package, Plus, Pencil, Trash2, Loader2, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const empty = {
  sku: "", name: "", description: "", category: "",
  photo_url: "", cost_price_eur: 0, sell_price_eur: 0,
  default_supplier_id: "", reorder_threshold: 5, reorder_qty: 10,
  active: true,
};

export default function ProductCatalogPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {mode, product}
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get("/inventory/products"),
        api.get("/inventory/suppliers"),
      ]);
      setItems(p.data.items || []);
      setSuppliers(s.data.items || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setForm(empty); setModal({ mode: "create" }); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setModal({ mode: "edit", product: p }); };

  const save = async () => {
    if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU et Nom requis"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_price_eur: Number(form.cost_price_eur) || 0,
        sell_price_eur: Number(form.sell_price_eur) || 0,
        reorder_threshold: Number(form.reorder_threshold) || 0,
        reorder_qty: Number(form.reorder_qty) || 1,
        default_supplier_id: form.default_supplier_id || null,
      };
      if (modal.mode === "create") {
        await api.post("/inventory/products", payload);
        toast.success("Produit créé");
      } else {
        await api.put(`/inventory/products/${modal.product.product_id}`, payload);
        toast.success("Produit mis à jour");
      }
      setModal(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.name} ?`)) return;
    try {
      await api.delete(`/inventory/products/${p.product_id}`);
      toast.success("Produit supprimé");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="product-catalog">
      <PageHeader
        icon={<Package />}
        title="Catalogue produits"
        description={`${items.length}/30 produits référencés.`}
        actions={
          <Button onClick={openCreate} disabled={items.length >= 30} data-testid="btn-create-product" className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un produit
          </Button>
        }
      />

      {items.length === 0 && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-12 text-center text-slate-500">
          Aucun produit. Cliquez "Ajouter un produit" ou synchronisez depuis Shopify.
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="bg-[#0b0811] text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left p-3 w-16"></th>
                <th className="text-left p-3">SKU</th>
                <th className="text-left p-3">Nom</th>
                <th className="text-right p-3">Stock</th>
                <th className="text-right p-3">Prix achat</th>
                <th className="text-right p-3">Prix vente</th>
                <th className="text-left p-3">Fournisseur</th>
                <th className="text-left p-3">Seuil</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const isLow = (p.stock_total || 0) <= (p.reorder_threshold || 0);
                const supName = suppliers.find(s => s.supplier_id === p.default_supplier_id)?.name || "—";
                return (
                  <tr key={p.product_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`product-${p.product_id}`}>
                    <td className="p-3">
                      {p.photo_url ? (
                        <img src={p.photo_url} alt={p.name} className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-white/5 rounded flex items-center justify-center"><ImageIcon className="w-4 h-4 text-slate-600" /></div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-300">{p.sku}</td>
                    <td className="p-3">
                      <div className="text-white font-medium">{p.name}</div>
                      {p.category && <div className="text-[10px] text-slate-500">{p.category}</div>}
                      {p.shopify_product_id && <span className="inline-block text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 mt-1">Shopify</span>}
                    </td>
                    <td className="p-3 text-right">
                      <span className={`font-bold ${isLow ? "text-red-400" : "text-white"}`}>{p.stock_total ?? 0}</span>
                      {isLow && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-400" />}
                    </td>
                    <td className="p-3 text-right text-slate-300">{p.cost_price_eur?.toFixed(2)} €</td>
                    <td className="p-3 text-right text-white font-bold">{p.sell_price_eur?.toFixed(2)} €</td>
                    <td className="p-3 text-xs text-slate-400">{supName}</td>
                    <td className="p-3 text-xs text-slate-500">{p.reorder_threshold}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-${p.product_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`delete-${p.product_id}`} className="hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal CRUD */}
      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{modal?.mode === "create" ? "Nouveau produit" : "Modifier le produit"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="SKU *"><Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} data-testid="field-sku" /></Field>
            <Field label="Nom *"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="field-name" /></Field>
            <Field label="Catégorie"><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></Field>
            <Field label="Photo (URL)"><Input value={form.photo_url} onChange={e => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." /></Field>
            <Field label="Prix d'achat (€)"><Input type="number" step="0.01" value={form.cost_price_eur} onChange={e => setForm({ ...form, cost_price_eur: e.target.value })} data-testid="field-cost" /></Field>
            <Field label="Prix de vente (€)"><Input type="number" step="0.01" value={form.sell_price_eur} onChange={e => setForm({ ...form, sell_price_eur: e.target.value })} data-testid="field-sell" /></Field>
            <Field label="Fournisseur par défaut">
              <select className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-2 text-sm" value={form.default_supplier_id || ""} onChange={e => setForm({ ...form, default_supplier_id: e.target.value })} data-testid="field-supplier">
                <option value="">—</option>
                {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Description">
              <textarea className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-2 text-sm min-h-[60px]" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </Field>
            <Field label="Seuil de réappro"><Input type="number" value={form.reorder_threshold} onChange={e => setForm({ ...form, reorder_threshold: e.target.value })} data-testid="field-threshold" /></Field>
            <Field label="Qté à recommander"><Input type="number" value={form.reorder_qty} onChange={e => setForm({ ...form, reorder_qty: e.target.value })} data-testid="field-reorder-qty" /></Field>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setModal(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#f7c935] hover:bg-[#e6b825] text-[#14101c]" data-testid="btn-save-product">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);
